import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, Typography, Breadcrumb, Popconfirm, message, Select, Tooltip, Row, Col, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, SyncOutlined, EyeOutlined } from '@ant-design/icons';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { getCropsApi, deleteCropApi, changeCropStatusApi, getPlotsApi, getCropsByPlotIdApi, getFarmListApi } from '../../api/crops';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { AREA_UNITS, CROP_NAME_IMAGE, CROP_PHOTO } from '../../utils/constants';
import HarvestModal from '../../components/HarvestModal';
import AddPlotModal from '../Plots/AddPlotModal';
import PlotMiniMap from '../Plots/PlotMiniMap';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const currentYear = dayjs().year();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => ({ value: String(currentYear - i), label: String(currentYear - i) }));
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 1, label: 'Active' },
  { value: 0, label: 'Harvested' },
];

export default function Crops() {
  const { farmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [plots, setPlots] = useState([]);
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState(() => {
    const saved = sessionStorage.getItem(`crops_years_${farmId}`);
    const parentYears = location.state?.years;
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parentYears) {
        const filtered = parsed.filter((y) => parentYears.includes(y));
        return filtered.length > 0 ? filtered : parentYears;
      }
      return parsed;
    }
    return parentYears || [String(currentYear)];
  });
  const [cropStatus, setCropStatus] = useState('');
  const [farmName, setFarmName] = useState('');
  const [farmLocation, setFarmLocation] = useState('');
  const [addPlotOpen, setAddPlotOpen] = useState(false);
  const [harvestingCrop, setHarvestingCrop] = useState(null);

  useEffect(() => {
    getFarmListApi()
      .then((res) => {
        const list = res.data || [];
        const farm = list.find((f) => String(f.id) === String(farmId));
        if (farm?.name) setFarmName(farm.name);
        if (farm?.location) setFarmLocation(farm.location);
      })
      .catch(() => { });
  }, [farmId]);

  const fetchPlots = useCallback(async () => {
    try {
      const res = await getPlotsApi({ farm_id: farmId });
      setPlots(res.data?.data || []);
    } catch { }
  }, [farmId]);

  const fetchCrops = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (selectedPlotId) {
        const res = await getCropsByPlotIdApi(selectedPlotId);
        const raw = res.data?.data ?? res.data;
        data = Array.isArray(raw) ? raw : [];
      } else {
        const params = { farm_id: farmId, years, isFromWeb: 1 };
        if (cropStatus !== '') params.cropStatus = cropStatus;
        const res = await getCropsApi(params);
        data = res.data?.data || [];
      }
      setCrops(data.sort((a, b) => {
        const aActive = a.status === 1 || a.status === '1' ? 0 : 1;
        const bActive = b.status === 1 || b.status === '1' ? 0 : 1;
        return aActive - bActive;
      }));
    } catch {
      message.error('Failed to load crops');
    } finally {
      setLoading(false);
    }
  }, [farmId, years, cropStatus, selectedPlotId]);

  useEffect(() => { sessionStorage.setItem(`crops_years_${farmId}`, JSON.stringify(years)); }, [years, farmId]);
  useEffect(() => { fetchPlots(); }, [fetchPlots]);
  useEffect(() => { fetchCrops(); }, [fetchCrops]);

  const goToAddCrop = () => {
    if (plots.length === 0) { setAddPlotOpen(true); return; }
    navigate(`/farms/${farmId}/farm/add-crop`, {
      state: { plots, selectedPlotId, selectedPlot: plots.find((p) => p.id === selectedPlotId) || null },
    });
  };

  const handleDelete = async (cropId) => {
    try {
      await deleteCropApi({ crop_id: cropId });
      message.success('Crop deleted');
      fetchCrops();
    } catch {
      message.error('Failed to delete crop');
    }
  };

  const columns = [
    {
      title: t('crop_name'),
      dataIndex: 'crop_name',
      key: 'crop_name',
      render: (name, record) => (
        <Button type="link" style={{ padding: 0, color: '#1a6b2a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={() => navigate(`/farms/${farmId}/farm/${record.id}/expenses`, { state: { crop: { crop_name: record.crop_name || record.crop, verity: record.verity, area: record.area, area_unit: record.area_unit, image: record.image, plot_name: record.plot_name, farm_name: farmName, crop_date: record.crop_date, harvest_date: record.harvest_date }, years } })}>
          {record.image ? (
            <img src={CROP_NAME_IMAGE(record.image)} alt={name} style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
          ) : <span>🌱</span>}
          <div style={{ textAlign: 'left' }}>
            <div>{name || record.crop}</div>
            {record.verity && <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{record.verity}</div>}
          </div>
        </Button>
      ),
    },
    { title: t('area'), key: 'area', render: (_, r) => `${r.area} ${AREA_UNITS[r.area_unit] || r.area_unit}` },
    {
      title: t('sowing_date'),
      dataIndex: 'crop_date',
      render: (v, record) => {
        if (!v) return '—';
        const today = dayjs();
        const hd = record.harvest_date ? dayjs(record.harvest_date) : null;
        const endDate = hd && !hd.isAfter(today, 'day') ? hd : today;
        const days = endDate.diff(dayjs(v), 'day');
        return (
          <div>
            <div>{formatDate(v)}</div>
            <div style={{ fontSize: 12, color: '#1a6b2a', marginTop: 2, fontWeight: 500 }}>crop age: {days} days</div>
          </div>
        );
      },
    },
    { title: t('harvest_date'), dataIndex: 'harvest_date', render: formatDate },
    {
      title: t('status'),
      dataIndex: 'status',
      render: (s, record) => {
        const isActive = s === 1 || s === '1';
        const noHarvestDate = !record.harvest_date;
        const harvestDue = record.harvest_date && !dayjs().isBefore(dayjs(record.harvest_date), 'day');
        const showMarkBtn = isActive && (noHarvestDate || harvestDue);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
            <Tag color={isActive ? 'green' : 'orange'} icon={isActive ? <SyncOutlined /> : <CheckCircleOutlined />}>
              {isActive ? t('active') : t('harvested')}
            </Tag>
            {showMarkBtn && (
              <Button
                size="small"
                style={{ fontSize: 11, borderColor: '#d46b08', color: '#d46b08', height: 22, padding: '0 6px' }}
                onClick={() => setHarvestingCrop(record)}
              >
                Mark Harvested
              </Button>
            )}
          </div>
        );
      },
    },
    { title: t('total_expense'), dataIndex: 'kharch', render: (v) => <span style={{ color: '#fa8c16' }}>{formatCurrency(v)}</span> },
    { title: t('total_selling'), dataIndex: 'vechan', render: (v) => <span style={{ color: '#52c41a' }}>{formatCurrency(v)}</span> },
    {
      title: 'Net Profit / Loss', key: 'profit',
      render: (_, r) => {
        const p = (r.vechan || 0) - (r.kharch || 0);
        return <span style={{ color: p >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>{formatCurrency(p)}</span>;
      },
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
              onClick={() => navigate(`/farms/${farmId}/farm/${record.id}/expenses`, { state: { crop: { crop_name: record.crop_name || record.crop, verity: record.verity, area: record.area, area_unit: record.area_unit, image: record.image, plot_name: record.plot_name, farm_name: farmName, crop_date: record.crop_date, harvest_date: record.harvest_date }, years } })} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${record.id}/edit-crop`, { state: { editData: record, plots } })} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this crop?" onConfirm={() => handleDelete(record.id)} okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const totalExpense = crops.reduce((s, c) => s + (Number(c.kharch) || 0), 0);
  const totalSelling = crops.reduce((s, c) => s + (Number(c.vechan) || 0), 0);
  const profit = totalSelling - totalExpense;

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[
        { title: <Link to="/farms" state={{ years: location.state?.years }}>Farms</Link> },
        { title: farmName },
      ]} />

      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, textTransform: 'capitalize' }}>{farmName}</Title>
      </div>

      {/* Horizontal Plot Selector */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8, color: '#595959' }}>📍 Plots</Text>
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', overflow: 'hidden' }}>
          {/* Scrollable plot cards */}
          <div style={{ overflowX: 'auto', paddingBottom: 8, flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, minWidth: 'max-content', alignItems: 'stretch' }}>
              {plots.map((plot) => (
                <div
                  key={plot.id}
                  onClick={() => setSelectedPlotId(plot.id === selectedPlotId ? null : plot.id)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: 8,
                    border: `2px solid ${selectedPlotId === plot.id ? '#1a6b2a' : '#d9d9d9'}`,
                    background: selectedPlotId === plot.id ? '#f0f9f0' : '#fff',
                    overflow: 'hidden',
                    width: 160,
                    flexShrink: 0,
                    userSelect: 'none',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <PlotMiniMap path={plot.path} activeCrop={!!plot.latest_crop_name} />
                  <div style={{
                    padding: '6px 10px',
                    fontWeight: selectedPlotId === plot.id ? 600 : 400,
                    color: selectedPlotId === plot.id ? '#1a6b2a' : '#333',
                    fontSize: 13,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}>
                    📍 {plot.name}
                    {plot.seted_area && (
                      <span style={{ color: '#8c8c8c', fontSize: 11, marginLeft: 4 }}>
                        {plot.seted_area} {AREA_UNITS[plot.seted_area_unit] || plot.seted_area_unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add Plot — fixed right */}
          <div
            onClick={() => setAddPlotOpen(true)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 90,
              borderRadius: 8,
              border: '2px dashed #1a6b2a',
              color: '#1a6b2a',
              gap: 4,
              fontSize: 12,
              padding: '8px 0',
              userSelect: 'none',
              flexShrink: 0,
            }}
          >
            <PlusOutlined style={{ fontSize: 18 }} />
            Add Plot
          </div>
        </div>
      </div>

      {/* Totals */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: t('total_expense'), value: totalExpense, color: '#fa8c16', bg: '#fff7e6' },
          { label: t('total_selling'), value: totalSelling, color: '#52c41a', bg: '#f6ffed' },
          { label: profit >= 0 ? t('net_profit') : 'Net Loss', value: profit, color: profit >= 0 ? '#52c41a' : '#ff4d4f', bg: profit >= 0 ? '#f6ffed' : '#fff1f0' },
        ].map((item) => (
          <Col xs={24} sm={8} key={item.label}>
            <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
              <Statistic title={item.label} value={formatCurrency(Math.abs(item.value))} valueStyle={{ color: item.color, fontSize: 18, fontWeight: 'bold' }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Crops Table */}
      <Card
        style={{ borderRadius: 10 }}
        title={
          <div>
            <div>Crop List</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#888', marginTop: 2 }}>
              {selectedPlotId
                ? plots.find((p) => p.id === selectedPlotId)?.name || 'Selected Plot'
                : `All crops of farm ${farmName}`}
            </div>
          </div>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={goToAddCrop}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
            {t('add_crop')}
          </Button>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <Select mode="multiple" value={years} onChange={setYears} options={YEAR_OPTIONS} placeholder="Year" style={{ minWidth: 160 }} />
            <Select value={cropStatus} onChange={setCropStatus} options={STATUS_OPTIONS} style={{ width: 120 }} />
          </Space>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : crops.length === 0 ? (
          plots.length === 0 ? (
            <Empty description="Add a plot first to start adding crops">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddPlotOpen(true)}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Add Plot</Button>
            </Empty>
          ) : (
            <Empty description="No crops found">
              <Button type="primary" icon={<PlusOutlined />} onClick={goToAddCrop}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('add_crop')}</Button>
            </Empty>
          )
        ) : (
          <Table dataSource={crops} columns={columns} rowKey="id" scroll={{ x: 900 }}
            onRow={(record) => ({ onClick: () => navigate(`/farms/${farmId}/farm/${record.id}/expenses`, { state: { crop: { crop_name: record.crop_name || record.crop, verity: record.verity, area: record.area, area_unit: record.area_unit, image: record.image, plot_name: record.plot_name, farm_name: farmName, crop_date: record.crop_date, harvest_date: record.harvest_date }, years } }), style: { cursor: 'pointer' } })} />
        )}
      </Card>

      <AddPlotModal
        open={addPlotOpen}
        onClose={() => setAddPlotOpen(false)}
        onSuccess={() => { setAddPlotOpen(false); fetchPlots(); }}
        defaultFarmId={parseInt(farmId)}
        defaultFarmLocation={farmLocation}
      />

      <HarvestModal
        open={!!harvestingCrop}
        crop={harvestingCrop}
        onClose={() => setHarvestingCrop(null)}
        onSave={async (payload) => {
          await changeCropStatusApi({
            crop_id: harvestingCrop.id,
            status: payload.status,
            harvest_date: payload.harvest_date,
          });
          message.success('Crop updated');
          setHarvestingCrop(null);
          fetchCrops();
        }}
      />
    </div>
  );
}
